import { z } from "zod";
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const label = (max) => z
    .string()
    .trim()
    .min(2)
    .max(max)
    .regex(/^[^\p{C}]+$/u);
const fanPower = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,2})(?:\.\d{1,6})?$/)
    .refine((value) => Number(value) > 0 && Number(value) <= 125, "This review supports only incorporated fan motors above zero and at most125W");
const washerCapacity = z
    .string()
    .regex(/^(?:0\.(?=\d{0,5}[1-9])\d{1,6}|[1-9](?:\.\d{1,6})?|10(?:\.0{1,6})?)$/, "This review supports only household washers above zero and at most10kg");
/** Physical observations only. Legal classifications and rates are server derived. */
export const applianceReviewedPhysicalAttributesSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("FAN"),
        household: z.literal(true),
        fanMount: z.literal("FLOOR"),
        powerW: fanPower,
    })
        .strict(),
    z
        .object({
        kind: z.literal("EVAPORATIVE_AIR_COOLER"),
        evaporative: z.literal(true),
    })
        .strict(),
    z
        .object({
        kind: z.literal("WASHER"),
        household: z.literal(true),
        washerType: z.literal("OTHER"),
        capacityKg: washerCapacity,
    })
        .strict(),
]);
export const appliancePhysicalReviewDocumentSchema = z
    .object({
    evidenceId: z.string().uuid(),
    contentSha256: hash,
    pageReferences: z
        .array(z
        .object({
        page: z.number().int().min(1).max(8000),
        section: label(300),
    })
        .strict())
        .min(1)
        .max(16),
})
    .strict();
export const appliancePhysicalReviewCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    sourceRowSha256: hash,
    documentBrand: label(100),
    reviewedModel: label(160),
    attributes: applianceReviewedPhysicalAttributesSchema,
    documents: z.array(appliancePhysicalReviewDocumentSchema).min(1).max(4),
    reason: label(2000).refine((value) => value.length >= 10),
    sourceReference: label(2000).refine((value) => value.length >= 10),
    validFrom: z.string().date(),
    validTo: z.string().date().nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.validTo !== null && value.validTo < value.validFrom)
        ctx.addIssue({ code: "custom", path: ["validTo"], message: "End date precedes start date" });
    if (new Set(value.documents.map((document) => document.evidenceId)).size !==
        value.documents.length)
        ctx.addIssue({ code: "custom", path: ["documents"], message: "Evidence IDs must be unique" });
    for (const [index, document] of value.documents.entries())
        if (new Set(document.pageReferences.map((page) => `${page.page}:${page.section}`)).size !==
            document.pageReferences.length)
            ctx.addIssue({
                code: "custom",
                path: ["documents", index, "pageReferences"],
                message: "Page references must be unique",
            });
});
export const appliancePhysicalReviewRecordSchema = z
    .object({
    version: z.literal("PRODUCT_PHYSICAL_ATTRIBUTE_REVIEW_V1"),
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    uploadId: z.string().uuid(),
    itemId: label(160),
    status: z.literal("DOCUMENT_REVIEWED"),
    deliveryRole: z.literal("DISTRIBUTOR_COPY"),
    observationKind: z.literal("ADMIN_DOCUMENT_REVIEW"),
    gtinVerified: z.literal(false),
    fiscalApproval: z.literal("NOT_GRANTED"),
    review: appliancePhysicalReviewCreateSchema,
    originalNcm: z
        .string()
        .regex(/^\d{8}$/)
        .nullable(),
    derivedNcm: z.enum(["84145190", "84796000", "84501900"]),
    classificationRelation: z.enum(["MISSING_NCM", "MATCHING_NCM", "CONFLICTING_DECLARED_NCM"]),
    reviewedBy: z.string().uuid(),
    reviewedAt: z.string().datetime(),
    reviewSha256: hash,
    receiptSha256: hash,
})
    .strict();
export const appliancePhysicalReviewListSchema = z
    .object({
    items: z.array(appliancePhysicalReviewRecordSchema).max(100),
})
    .strict();
//# sourceMappingURL=appliance-physical-reviews.js.map